<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import EditTitleModal from '@/components/pageContent/modules/EditTitleModal.vue';
import EditDescriptionModal from '@/components/pageContent/modules/EditDescriptionModal.vue';
import MediaSliderModal from '@/components/pageContent/modules/MediaSliderModal.vue';
import publishGuardMixin from '@/mixins/publishGuardMixin';

export default {
  mixins: [publishGuardMixin],
  name: 'CompetitorsTab',
  components: {
    PencilIcon,
    EditTitleModal,
    EditDescriptionModal,
    MediaSliderModal,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update'],
  data() {
    return {
      videoRefs: [],
      titleRefs: [],
      // Modal state
      titleModalVisible: false,
      descriptionModalVisible: false,
      mediaModalVisible: false,
      editingIndex: null,
    };
  },
  computed: {
    editingItem() {
      if (this.editingIndex === null) return null;
      return this.items[this.editingIndex] || null;
    },
    editingItemIsVideo() {
      return this.editingItem?.type === 'video';
    },
    editingItemVideoUrl() {
      if (this.editingItem?.type === 'video') {
        return this.editingItem?.url || '';
      }
      return '';
    },
    mediaModalSlides() {
      if (!this.editingItem || this.editingItem.type === 'video') {
        return [{ imageUrl: '', imageUrlMobile: '' }];
      }
      return [{
        imageUrl: this.editingItem?.url || '',
        imageUrlMobile: this.editingItem?.urlMobile || '',
      }];
    },
  },
  mounted() {
    this.setupResizeObserver();
    this.setupVideoSync();
    this.setupAutoPlay();
  },
  beforeUnmount() {
    this.cleanup();
  },
  methods: {
    b: bem('competitors-tab'),
    // Modal methods
    openTitleModal(index) {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.editingIndex = index;
        this.titleModalVisible = true;
      }
    },
    closeTitleModal() {
      this.titleModalVisible = false;
      this.editingIndex = null;
    },
    openDescriptionModal(index) {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.editingIndex = index;
        this.descriptionModalVisible = true;
      }
    },
    closeDescriptionModal() {
      this.descriptionModalVisible = false;
      this.editingIndex = null;
    },
    openMediaModal(index) {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.editingIndex = index;
        this.mediaModalVisible = true;
      }
    },
    closeMediaModal() {
      this.mediaModalVisible = false;
      this.editingIndex = null;
    },
    // Save handlers
    handleTitleSave(values) {
      if (this.editingIndex === null) return;
      const newItems = [...this.items];
      newItems[this.editingIndex] = {
        ...newItems[this.editingIndex],
        title: values.title,
        titleMobile: values.titleMobile,
      };
      this.$emit('update', { items: newItems });
      this.closeTitleModal();
    },
    handleTitleDelete() {
      if (this.editingIndex === null) return;
      const newItems = [...this.items];
      newItems[this.editingIndex] = {
        ...newItems[this.editingIndex],
        title: '',
        titleMobile: '',
      };
      this.$emit('update', { items: newItems });
      this.closeTitleModal();
    },
    handleDescriptionSave(values) {
      if (this.editingIndex === null) return;
      const newItems = [...this.items];
      newItems[this.editingIndex] = {
        ...newItems[this.editingIndex],
        description: values.description,
        descriptionMobile: values.descriptionMobile,
      };
      this.$emit('update', { items: newItems });
      this.closeDescriptionModal();
    },
    handleDescriptionDelete() {
      if (this.editingIndex === null) return;
      const newItems = [...this.items];
      newItems[this.editingIndex] = {
        ...newItems[this.editingIndex],
        description: '',
        descriptionMobile: '',
      };
      this.$emit('update', { items: newItems });
      this.closeDescriptionModal();
    },
    handleMediaSave(result) {
      if (this.editingIndex === null) return;
      const newItems = [...this.items];
      if (result.isShowVideo) {
        // Video mode
        newItems[this.editingIndex] = {
          ...newItems[this.editingIndex],
          type: 'video',
          url: result.videoUrl,
          urlMobile: result.videoUrl,
        };
      } else {
        // Image mode
        const slide = result.blocks?.[0] || {};
        newItems[this.editingIndex] = {
          ...newItems[this.editingIndex],
          type: 'image',
          url: slide.imageUrl || '',
          urlMobile: slide.imageUrlMobile || '',
        };
      }
      this.$emit('update', { items: newItems });
      this.closeMediaModal();
    },
    // Video/media methods
    setVideoRef(element, index) {
      if (element) {
        this.videoRefs[index] = element;
      }
    },
    setupResizeObserver() {
      this.resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const titleEl = entry.target;
          const width = titleEl.offsetWidth;
          titleEl.style.setProperty('--title-width', `${width}px`);
        });
      });

      this.$nextTick(() => {
        this.titleRefs = this.$el.querySelectorAll('[class*="process-title"]');
        this.titleRefs.forEach((titleEl) => {
          if (titleEl) {
            this.resizeObserver.observe(titleEl);
          }
        });
      });
    },
    setupVideoSync() {
      if (!this.videoRefs.length) return;

      this.syncTimer = setInterval(() => {
        const loadedVideos = this.videoRefs.filter((ref) => ref && !ref.paused);
        if (loadedVideos.length < 2) return;

        let needsSync = false;
        const firstTime = loadedVideos[0].currentTime;

        for (let i = 1; i < loadedVideos.length; i += 1) {
          if (Math.abs(firstTime - loadedVideos[i].currentTime) > 0.1) {
            needsSync = true;
            break;
          }
        }

        if (needsSync) {
          loadedVideos.forEach((videoEl) => {
            // eslint-disable-next-line no-param-reassign
            videoEl.currentTime = firstTime;
          });
        }
      }, 800);
    },
    setupAutoPlay() {
      const videos = this.videoRefs.filter((ref) => ref);
      if (!videos.length) return;

      const allLoaded = videos.every((video) => video.readyState >= 3);

      if (allLoaded) {
        videos.forEach((video) => {
          video.play().catch(() => {
            // Ignore autoplay errors
          });
        });
      } else {
        const handleCanPlay = () => {
          const allCanPlay = videos.every((video) => video.readyState >= 3);

          if (allCanPlay) {
            videos.forEach((video) => {
              video.play().catch(() => {
                // Ignore errors
              });
            });

            videos.forEach((video) => {
              video.removeEventListener('canplay', handleCanPlay);
            });
          }
        };

        videos.forEach((video) => {
          video.addEventListener('canplay', handleCanPlay);
        });
      }
    },
    cleanup() {
      if (this.resizeObserver) {
        this.titleRefs.forEach((titleEl) => {
          if (titleEl) {
            this.resizeObserver.unobserve(titleEl);
          }
        });
        this.resizeObserver.disconnect();
      }

      if (this.syncTimer) {
        clearInterval(this.syncTimer);
      }
    },
  },
};
</script>

<template>
  <div :class="b('process', { isMobileView, isEditMode })">
    <div
      v-for="(item, index) in items"
      :key="item.id"
      :class="b('process-item', { isMobileView })"
    >
      <div
        :class="b('process-image', { isMobileView, editable: isEditMode })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openMediaModal(index)"
        @keyup.enter="openMediaModal(index)"
        @keyup.space="openMediaModal(index)"
      >
        <video
          v-if="item.type === 'video'"
          :ref="(el) => setVideoRef(el, index)"
          :src="item.url"
          :class="b('video', { isMobileView })"
          width="100%"
          loop
          muted
          preload="auto"
          playsinline
          :controls="!isEditMode"
        >
          <source :src="item.url" type="video/mp4" />
          <track kind="captions" />
        </video>
        <audio
          v-else-if="item.type === 'audio'"
          :src="item.url"
          :class="b('audio', { isMobileView })"
          :controls="!isEditMode"
          loop
          muted
          preload="auto"
          playsinline
          width="100%"
        >
          <source :src="item.url" type="audio/mp3" />
          <track kind="captions" />
        </audio>
        <img
          v-else-if="item.type === 'image' && item.url"
          :src="item.url"
          :alt="item.title"
          :class="b('image', { isMobileView })"
        />
        <!-- Placeholder when no media -->
        <div v-else :class="b('media-placeholder', { isMobileView })">
          <template v-if="isEditMode">
            <span :class="b('placeholder-plus')">+</span>
            CLICK TO UPLOAD
          </template>
        </div>
      </div>

      <!-- Mobile title (positioned over image) -->
      <h4
        v-if="isMobileView && item.title"
        :class="b('process-title', { odd: index % 2 === 0, even: index % 2 !== 0, isMobileView })"
      >
        {{ item.title }}
      </h4>

      <!-- Title (desktop only) -->
      <div
        v-if="!isMobileView"
        :class="b('title-wrap', {
          editable: isEditMode,
          odd: index % 2 === 0,
          even: index % 2 !== 0,
        })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openTitleModal(index)"
        @keyup.enter="openTitleModal(index)"
        @keyup.space="openTitleModal(index)"
      >
        <h4
          v-if="item.title"
          :class="b('process-title', { odd: index % 2 === 0, even: index % 2 !== 0 })"
        >
          {{ item.title }}
        </h4>
        <div v-else-if="isEditMode" :class="b('add-field')">
          + Add title
        </div>
        <PencilIcon
          v-if="isEditMode && item.title"
          :class="b('edit-icon')"
          color="#293241"
          :size="16"
        />
      </div>

      <!-- Description -->
      <div
        :class="b('description-wrap', { isMobileView, editable: isEditMode })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openDescriptionModal(index)"
        @keyup.enter="openDescriptionModal(index)"
        @keyup.space="openDescriptionModal(index)"
      >
        <p v-if="item.description" :class="b('process-description', { isMobileView })">
          {{ item.description }}
        </p>
        <div v-else-if="isEditMode" :class="b('add-field')">
          + Add description
        </div>
        <PencilIcon
          v-if="isEditMode && item.description"
          :class="b('edit-icon')"
          :color="isMobileView ? '#ffffff' : '#5e5d5d'"
          :size="16"
        />
      </div>
    </div>

    <!-- Modals -->
    <EditTitleModal
      :visible="titleModalVisible"
      :title="editingItem?.title || ''"
      :title-mobile="editingItem?.titleMobile || editingItem?.title || ''"
      :modal-title="`Edit Item ${editingIndex !== null ? editingIndex + 1 : ''} Title`"
      @close="closeTitleModal"
      @save="handleTitleSave"
      @delete="handleTitleDelete"
    />

    <EditDescriptionModal
      :visible="descriptionModalVisible"
      :description="editingItem?.description || ''"
      :description-mobile="editingItem?.descriptionMobile || editingItem?.description || ''"
      :title="`Edit Item ${editingIndex !== null ? editingIndex + 1 : ''} Description`"
      @close="closeDescriptionModal"
      @save="handleDescriptionSave"
      @delete="handleDescriptionDelete"
    />

    <MediaSliderModal
      :visible="mediaModalVisible"
      :slides="mediaModalSlides"
      :is-show-video="editingItemIsVideo"
      :video-url="editingItemVideoUrl"
      :single-mode="true"
      @close="closeMediaModal"
      @save="handleMediaSave"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/media.scss';

.competitors-tab {
  & * {
    font-family: Montserrat, sans-serif;
  }

  &__process {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    width: 100%;
    padding: 16px 24px 27px 24px;
    gap: 20px;
    min-width: 1544px;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(16) vw-full(24) vw-full(27) vw-full(24);
      gap: vw-full(20);
      min-width: vw-full(1544);
    }

    @include media($small) {
      padding: 0;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0;
      min-width: 100%;
    }

    &_isMobileView {
      padding: 0;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0;
      min-width: 100%;
    }
  }

  &__edit-icon {
    flex-shrink: 0;
    opacity: 0.8;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }
  }

  &__add-field {
    @include add-field-button;
  }

  &__title-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8);
    }

    &_editable {
      cursor: pointer;
      padding: 4px 8px;
      margin: -4px -8px;
      border-radius: 5px;
      transition: background 0.2s ease;

      @include mediaMinMax(1025, $normal) {
        padding: vw-full(4) vw-full(8);
        margin: vw-full(-4) vw-full(-8);
        border-radius: vw-full(5);
      }

      &:hover {
        background: rgba(0, 0, 0, 0.05);

        .competitors-tab__edit-icon {
          opacity: 1;
        }
      }

      &_isMobileView:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }

  &__description-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8);
    }

    &_editable {
      cursor: pointer;
      padding: 4px 8px;
      margin: -4px -8px;
      border-radius: 5px;
      transition: background 0.2s ease;

      @include mediaMinMax(1025, $normal) {
        padding: vw-full(4) vw-full(8);
        margin: vw-full(-4) vw-full(-8);
        border-radius: vw-full(5);
      }

      &:hover {
        background: rgba(0, 0, 0, 0.05);

        .competitors-tab__edit-icon {
          opacity: 1;
        }
      }

      &_isMobileView:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }

  &__process-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    border-radius: 12px;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    gap: 24px;

    @include mediaMinMax(1025, $normal) {
      border-radius: vw-full(12);
      padding: vw-full(24) vw-full(16);
      gap: vw-full(24);
    }

    &_isMobileView {
      position: relative;
      background-color: #293241;
      gap: 0;
    }
  }

  &__process-image {
    border-radius: 12px 3px;
    background: #d9d9d9;
    box-shadow: 4px 4px 8px 0 rgba(95, 138, 164, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;

    @include mediaMinMax(1025, $normal) {
      border-radius: vw-full(12) vw-full(3);
    }

    &_editable {
      cursor: pointer;

      &:hover {
        .competitors-tab__image,
        .competitors-tab__video {
          filter: brightness(0.7);
        }
      }
    }

    &_isMobileView {
      box-shadow: none;
      margin-bottom: 0;
      border-radius: 36px 36px 0 0;

      &::after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          180deg,
          rgba(41, 50, 65, 0) 69.29%,
          #293241 98.97%
        );
        z-index: 1;
      }
    }
  }

  &__media-placeholder {
    width: 487px;
    height: 372px;
    background: #d9d9d9;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #7f7f7f;
    font-family: Montserrat, sans-serif;
    font-size: 20px;
    font-weight: 400;
    text-transform: uppercase;
    text-align: center;
    line-height: 1.4;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(487);
      height: vw-full(372);
      font-size: vw-full(20);
      gap: vw-full(8);
    }

    @include media($small) {
      width: vw-mobile(356, 412);
      height: vw-mobile(232, 412);
    }

    &_isMobileView {
      width: 356px;
      height: 232px;
    }
  }

  &__placeholder-plus {
    font-size: 48px;
    line-height: 1;
    font-weight: 200;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(48);
    }
  }

  &__image,
  &__video {
    width: 487px;
    height: 372px;
    object-fit: cover;
    transition: filter 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(487);
      height: vw-full(372);
    }

    @include media($small) {
      width: vw-mobile(356, 412);
      height: vw-mobile(232, 412);
    }

    &_isMobileView {
      width: 356px;
      height: 232px;
    }
  }

  &__process-title {
    color: $ebony-clay-gray-color;
    text-align: center;
    font-size: 36px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    text-transform: capitalize;
    margin-bottom: 0;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(36);
    }

    @include media($small) {
      position: absolute;
      top: vw-mobile(208, 412);
      left: 0;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      width: 100%;
      padding: 0 vw-mobile(16, 412);
      color: $white-color;
      font-size: vw-mobile(20, 412);
      z-index: 1;
      margin-bottom: 0;
      gap: 12px;

      &::after {
        content: '';
        display: block;
        flex: 1;
        height: 1px;
        background: $white-color;
      }

      &::before {
        content: '';
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        display: block;
        width: vw-mobile(6, 412);
        height: vw-mobile(6, 412);
        border-radius: 50%;
        background: $white-color;
      }
    }

    &_isMobileView {
      position: absolute;
      top: 208px;
      left: 0;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      width: 100%;
      padding: 0 16px;
      color: $white-color;
      font-size: 20px;
      z-index: 1;
      margin-bottom: 0;
      gap: 12px;

      &::after {
        content: '';
        display: block;
        flex: 1;
        height: 1px;
        background: $white-color;
      }

      &::before {
        content: '';
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        display: block;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: $white-color;
      }
    }

    &_odd {
      @include media($small) {
        &::before {
          right: vw-mobile(16, 412);
        }
      }
    }

    &_odd#{&}_isMobileView {
      &::before {
        right: 16px;
      }
    }

    &_even {
      @include media($small) {
        flex-direction: row-reverse;

        &::before {
          left: vw-mobile(16, 412);
        }
      }
    }

    &_even#{&}_isMobileView {
      flex-direction: row-reverse;

      &::before {
        left: 16px;
      }
    }
  }

  &__process-description {
    color: $scorpion;
    font-size: 19px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    max-width: 460px;
    margin-bottom: 0;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(19);
      max-width: vw-full(460);
    }

    &_isMobileView {
      font-size: 16px;
      max-width: 316px;
      color: $white-color;
      margin: 20px auto 28px;
    }
  }
}
</style>
